'use client';

// module imports
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { z } from 'zod';

// file imports
import { FormField } from '@/components';
import { registerUser } from '@/redux/slices/authSlice';
import type { AppDispatch, RootState } from '@/redux/store';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email({ message: 'Please enter a valid email' }),
  password: z.string().min(1, 'Password is required').min(6, { message: 'Password must be at least 6 characters' }),
});

type RegisterFormData = z.infer<typeof schema>;

interface RegisterFormProps {
  onSuccess: (email: string) => void;
}

export default function RegisterForm({ onSuccess }: RegisterFormProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error } = useSelector((state: RootState) => state.auth);

  const {
    register,
    handleSubmit,
    reset: resetSignUp,
    formState: { errors: signUpErrors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      // Lowercase the email before sending
      const lowercasedEmail = data.email.toLowerCase();
      await dispatch(registerUser({ email: lowercasedEmail, password: data.password })).unwrap();
      resetSignUp();
      onSuccess(lowercasedEmail);
    } catch (err) {
      console.error('Registration error:', err);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <FormField id="email" label="Email Address" type="email" placeholder="Enter your email" {...register('email')} error={signUpErrors.email?.message} required />
      <div className="mt-2"></div>
      <FormField id="password" label="Password" type="password" placeholder="Enter your password" {...register('password')} error={signUpErrors.password?.message} required />
      {error && <div className="text-red-600 mb-3 text-sm">{error}</div>}
      <div className="mt-3"></div>
      <button type="submit" className="w-full py-3 bg-[#5167b2] text-white font-bold rounded-lg hover:bg-[#4158a2] cursor-pointer transition" disabled={loading}>
        {loading ? 'Creating Account...' : 'REGISTER'}
      </button>
    </form>
  );
}
